/**
 * Fills the uniform [data-pcard-rating] slot on product cards with a
 * consistent score + SVG stars + count, read from the real data Judge.me
 * already renders (data-average-rating / data-number-of-reviews on its
 * hidden .jdgm-prev-badge). The reviews app itself is never touched —
 * this only standardizes how its existing data is displayed.
 */

const STAR_PATH =
  'M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.27l-5.8 3.05 1.11-6.46-4.7-4.58 6.49-.94z';

/** @returns {string} */
function starsMarkup() {
  return `
    <span class="pcard-rating__star-empty">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
    </span>
    <span class="pcard-rating__star-fill">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
    </span>
  `;
}

/**
 * @param {number} rating
 * @returns {string}
 */
function buildStars(rating) {
  let html = '';
  for (let i = 1; i <= 5; i += 1) {
    const diff = rating - (i - 1);
    let fillPercent = 0;
    if (diff >= 1) fillPercent = 100;
    else if (diff >= 0.25) fillPercent = 50;
    html += `<span class="pcard-rating__star" style="--fill:${fillPercent}%">${starsMarkup()}</span>`;
  }
  return html;
}

/**
 * @param {Element} slot
 */
function fillSlot(slot) {
  const card = slot.closest('product-card');
  const badge = card ? card.querySelector('.jdgm-prev-badge') : null;
  const rating = badge ? parseFloat(badge.getAttribute('data-average-rating') || '0') : 0;
  const count = badge ? parseInt(badge.getAttribute('data-number-of-reviews') || '0', 10) : 0;

  if (count === 0) {
    slot.hidden = true;
    slot.innerHTML = '';
    slot.removeAttribute('role');
    slot.removeAttribute('aria-label');
    slot.setAttribute('aria-hidden', 'true');
    return;
  }

  slot.hidden = false;
  slot.innerHTML = `
    <span class="pcard-rating__score">${rating.toFixed(1)}</span>
    <span class="pcard-rating__stars">${buildStars(rating)}</span>
    <span class="pcard-rating__count">(${count})</span>
  `;
  slot.setAttribute('role', 'img');
  slot.removeAttribute('aria-hidden');
  slot.setAttribute('aria-label', `${rating.toFixed(1)} de 5 estrellas, ${count} reseña${count === 1 ? '' : 's'}`);
}

function run(root) {
  root.querySelectorAll('[data-pcard-rating]').forEach(fillSlot);
}

/**
 * Collection pages load more cards via infinite scroll/filters (morph),
 * and quick-add can render cards inside a modal — none of that is a full
 * page load, so a MutationObserver picks up any slot added after the
 * first pass instead of leaving it empty.
 */
function observeNewCards() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = /** @type {Element} */ (node);
        if (el.matches('[data-pcard-rating]')) fillSlot(el);
        el.querySelectorAll?.('[data-pcard-rating]').forEach(fillSlot);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    run(document);
    observeNewCards();
  });
} else {
  run(document);
  observeNewCards();
}
